import React, { useState, useEffect } from 'react';
import {
  Search,
  Truck,
  Calendar,
  Package,
  User,
  Trash2,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  X,
  ExternalLink,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { Button, Input, Card, Badge, LoadingSpinner, Notification } from './SharedComponents';
import axios from 'axios';
import moment from 'moment-timezone';

const API_URL = import.meta.env.VITE_API_URL;

function DeliveryLog() {
  // State management
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);

  // Filters
  const [showPalletScans, setShowPalletScans] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'CREATED_AT', direction: 'desc' });

  // Delete confirmation
  const [selectedTrackingCode, setSelectedTrackingCode] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  // Fetch delivery logs
  const fetchDeliveryLogs = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/api/delivery_log`);
      setLogs(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching delivery logs:', error);
      showNotification('Failed to load delivery logs', 'error');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveryLogs();
  }, []);

  // Filter and sort logs
  useEffect(() => {
    let filtered = logs.filter(log => {
      const matchesSearch = searchTerm === '' ||
        Object.values(log).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesPalletFilter = !showPalletScans ||
        (log.TRACKING_CODE && log.TRACKING_CODE.toLowerCase().includes('pallet'));

      return matchesSearch && matchesPalletFilter;
    });

    // Sort logs
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';

        if (sortConfig.direction === 'asc') {
          return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        } else {
          return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
        }
      });
    }

    setFilteredLogs(filtered);
    setPage(0); // Reset to first page when filtering
  }, [logs, searchTerm, showPalletScans, sortConfig]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevSort => ({
      key,
      direction: prevSort.key === key && prevSort.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Handle delete
  const handleDeleteLog = async (trackingCode) => {
    try {
      await axios.delete(`${API_URL}/api/delivery_log/${trackingCode}`);
      showNotification('Delivery log deleted successfully');
      setOpenDeleteDialog(false);
      fetchDeliveryLogs();
    } catch (error) {
      console.error('Error deleting delivery log:', error);
      showNotification('Failed to delete delivery log', 'error');
    }
  };

  // Get delivery status
  const getDeliveryStatus = (log) => {
    if (log.TRACKING_CODE?.toLowerCase().includes('pallet')) {
      return { variant: 'info', label: 'Pallet Delivery', icon: Truck };
    }
    if (log.SUPPLIER?.toLowerCase().includes('ups')) {
      return { variant: 'warning', label: 'UPS Delivery', icon: Package };
    }
    if (log.SUPPLIER?.toLowerCase().includes('fedex')) {
      return { variant: 'success', label: 'FedEx Delivery', icon: Package };
    }
    return { variant: 'default', label: 'Standard Delivery', icon: Package };
  };

  // Get pagination data
  const paginatedLogs = filteredLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredLogs.length / rowsPerPage);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Notification */}
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Delivery Log</h1>
            <p className="text-gray-600">Track and manage all warehouse deliveries and shipments</p>
          </div>
          <Button
            onClick={fetchDeliveryLogs}
            variant="outline"
            disabled={isLoading}
            className="flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search and Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <Input
                placeholder="Search by tracking code, supplier, company, notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
                className="text-base"
              />
            </div>

            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={showPalletScans}
                  onChange={(e) => setShowPalletScans(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span>Pallet deliveries only</span>
              </label>

              <div className="text-sm text-gray-500 whitespace-nowrap">
                {filteredLogs.length} of {logs.length} entries
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Delivery Logs Table */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Deliveries</h2>
            <Badge variant="default">
              {filteredLogs.length} total entries
            </Badge>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" className="mb-4" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Loading delivery logs...</h3>
              <p className="text-gray-500">Please wait while we fetch the latest data.</p>
            </div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-20">
            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Truck className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Delivery Logs Found</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {searchTerm || showPalletScans
                ? "No deliveries match your search criteria. Try adjusting your filters."
                : "No delivery logs have been recorded yet."}
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('TRACKING_CODE')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Tracking Code</span>
                        <Filter className="w-3 h-3" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('SUPPLIER')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Supplier</span>
                        <Filter className="w-3 h-3" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('COMPANY')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Company</span>
                        <Filter className="w-3 h-3" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('NOTES')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Notes</span>
                        <Filter className="w-3 h-3" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('CREATED_AT')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Date</span>
                        <Filter className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedLogs.map((log, index) => {
                    const status = getDeliveryStatus(log);
                    return (
                      <tr key={`${log.TRACKING_CODE}-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Package className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {log.TRACKING_CODE || 'N/A'}
                              </div>
                              {log.REFERENCE && (
                                <div className="text-xs text-gray-500">
                                  Ref: {log.REFERENCE}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Truck className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-900">
                              {log.SUPPLIER || 'N/A'}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-900">
                              {log.COMPANY || 'N/A'}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">
                            {log.NOTES || '-'}
                          </span>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-3 flex-shrink-0" />
                            <div>
                              <div className="text-sm text-gray-900">
                                {moment(log.CREATED_AT).subtract(5, 'hours').format('MMM D, YYYY')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {moment(log.CREATED_AT).subtract(5, 'hours').format('h:mm A')} EST
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <Badge variant={status.variant} className="flex items-center w-fit">
                            <status.icon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            {log.TRACKING_URL && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(log.TRACKING_URL, '_blank')}
                                className="p-2"
                                title="Track package"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTrackingCode(log.TRACKING_CODE);
                                setOpenDeleteDialog(true);
                              }}
                              className="p-2 text-red-600 hover:text-red-700"
                              title="Delete log entry"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredLogs.length)} of {filteredLogs.length} entries
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      className="flex items-center"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>

                    <span className="text-sm text-gray-700">
                      Page {page + 1} of {totalPages}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1}
                      className="flex items-center"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      {openDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Delivery Log</h3>
              </div>

              <p className="text-gray-600 mb-4">
                Are you sure you want to delete the delivery log for tracking code:
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-6">
                <code className="text-sm font-mono text-gray-900">{selectedTrackingCode}</code>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone.
              </p>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setOpenDeleteDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteLog(selectedTrackingCode)}
                  variant="danger"
                  className="flex-1"
                >
                  Delete Log
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default DeliveryLog;